import React from 'react';
import type { TemplateItem } from './template-gallery';
import NewTemplateGalleryTry from './new-template-gallery-try';
import Embed, { type EmbedProps } from './Embed';

type Props = EmbedProps & {
  onChange: (next: EmbedProps) => void;
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-sm font-medium text-gray-800">{children}</div>
);

const Toggle: React.FC<{ checked: boolean; onChange: () => void; label: string }>
  = ({ checked, onChange, label }) => (
  <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-3">
    <span className="text-sm text-gray-800">{label}</span>
    <button
      type="button"
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${checked ? 'bg-purple-600' : 'bg-gray-300'}`}
      onClick={onChange}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  </label>
);

const EmbedSettings: React.FC<Props> = ({ html = '', heightMode = 'fixed', fixedHeight = 360, bordered = true, radius = 12, shadow = false, allowScripts = true, allowSameOrigin = true, allowPopups = true, allowScroll = false, removeBg = false, responsiveFit = false, offsetX = 0, offsetY = 0, onChange }) => {
  const [form, setForm] = React.useState<EmbedProps>({ html, heightMode: 'fixed', fixedHeight, bordered, radius, shadow, allowScripts, allowSameOrigin, allowPopups, allowScroll, removeBg, responsiveFit, offsetX, offsetY });
  const [debouncedHtml, setDebouncedHtml] = React.useState<string>(html || '');
  const [heightStr, setHeightStr] = React.useState<string>(String(fixedHeight || 360));
  const [offXStr, setOffXStr] = React.useState<string>(String(offsetX || 0));
  const [offYStr, setOffYStr] = React.useState<string>(String(offsetY || 0));
  const [showTemplatesTry, setShowTemplatesTry] = React.useState<boolean>(false);
  const previewRef = React.useRef<HTMLDivElement>(null);
  const [dim, setDim] = React.useState<{ w: number; h: number }>({ w: 0, h: 0 });

  const detected = React.useMemo(() => {
    try {
      const tpl = document.createElement('template');
      tpl.innerHTML = debouncedHtml || '';
      const root = tpl.content;
      const items: Array<{ kind: string; label: string; detail?: string }> = [];
      const push = (kind: string, el: Element, detail?: string) => {
        const text = (el.getAttribute('aria-label') || el.getAttribute('title') || (el.textContent || '').trim() || el.tagName.toLowerCase()).slice(0, 120);
        items.push({ kind, label: text, detail });
      };
      const queryAll = (sel: string) => Array.prototype.slice.call(root.querySelectorAll(sel) || []);
      queryAll('[onclick]').forEach((el: Element) => push('onclick', el, 'Inline click handler'));
      queryAll('[onchange],[oninput],[onmouseover],[onmouseout],[onkeydown],[onkeyup]').forEach((el: Element) => push('event', el, 'Inline event'));
      queryAll('a[href^="javascript:"]').forEach((el: Element) => push('link', el, 'javascript: link'));
      queryAll('a[target], a[download]').forEach((el: Element) => push('link', el, 'Navigation action'));
      queryAll('button').forEach((el: Element) => push('button', el));
      queryAll('[role="button"]').forEach((el: Element) => push('button', el, 'ARIA button'));
      queryAll('input[type="submit"], button[type="submit"]').forEach((el: Element) => push('submit', el, 'Form submit'));
      queryAll('form').forEach((el: Element) => push('form', el, (el.getAttribute('action') || 'Submits data')));
      queryAll('[data-action], [data-click], [data-event]').forEach((el: Element) => push('data', el, 'Data action'));
      // Deduplicate by kind+label
      const seen = new Set<string>();
      return items.filter(it => { const k = it.kind + '::' + it.label; if (seen.has(k)) return false; seen.add(k); return true; });
    } catch {
      return [] as Array<{ kind: string; label: string; detail?: string }>;
    }
  }, [debouncedHtml]);

  React.useEffect(() => { setForm({ html, heightMode: 'fixed', fixedHeight, bordered, radius, shadow, allowScripts, allowSameOrigin, allowPopups, allowScroll, removeBg, responsiveFit, offsetX, offsetY }); setDebouncedHtml(html||''); setHeightStr(String(fixedHeight||360)); setOffXStr(String(offsetX||0)); setOffYStr(String(offsetY||0)); }, [html, fixedHeight, bordered, radius, shadow, allowScripts, allowSameOrigin, allowPopups, allowScroll, removeBg, responsiveFit, offsetX, offsetY]);

  // Debounce only the HTML for smoother preview while typing
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedHtml(form.html || ''), 300);
    return () => clearTimeout(t);
  }, [form.html]);

  // Keep height field in sync when parent updates fixedHeight (e.g., drag-resize)
  React.useEffect(() => {
    const next = Math.max(120, fixedHeight || 360);
    if ((form.fixedHeight || 0) !== next) {
      setForm(prev => ({ ...prev, fixedHeight: next }));
      setHeightStr(String(next));
    }
  }, [fixedHeight]);

  React.useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const ro = new (window as any).ResizeObserver((entries: any[]) => {
      const r = entries && entries[0] ? entries[0].contentRect : null;
      if (r) setDim({ w: Math.round(r.width || 0), h: Math.round(r.height || 0) });
    });
    ro.observe(el);
    return () => { try { ro.disconnect(); } catch {} };
  }, [previewRef]);

  const emit = (patch: Partial<EmbedProps>) => {
    const next = { ...form, ...patch, heightMode: 'fixed' as const };
    setForm(next);
    onChange(next);
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-purple-200 bg-purple-50 text-purple-900 p-3 text-xs">
        Paste trusted HTML only. The embed renders in a sandboxed iframe.
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <FieldLabel>HTML</FieldLabel>
          <button
            type="button"
            className="ml-2 inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
            onClick={()=> setShowTemplatesTry(true)}
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            Templates
          </button>
        </div>
        <textarea
          className="w-full h-40 rounded-md border border-gray-300 p-2 font-mono text-xs focus:ring-2 focus:ring-purple-500"
          placeholder="<!doctype html>..."
          value={form.html || ''}
          onChange={(e)=> setForm(prev => ({ ...prev, html: e.target.value }))}
          onBlur={()=> emit({ html: form.html || '' })}
        />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-gray-800 inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 text-purple-700">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" stroke="none"><path d="M13 2L3 14h7l-1 8 11-14h-7l1-8z"/></svg>
            </span>
            Detected events
          </div>
          <span className="text-xs text-gray-500">{detected.length} found</span>
        </div>
        {detected.length === 0 ? (
          <div className="mt-3 text-xs text-gray-500">No interactive elements were detected in your HTML.</div>
        ) : (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {detected.map((it, i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border border-gray-200 p-2">
                <span className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md ${it.kind==='button' ? 'bg-blue-100 text-blue-700' : it.kind==='form' ? 'bg-amber-100 text-amber-700' : it.kind==='submit' ? 'bg-emerald-100 text-emerald-700' : it.kind==='link' ? 'bg-sky-100 text-sky-700' : it.kind==='onclick' ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-gray-100 text-gray-700'}`}>
                  {it.kind==='button' ? (
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="9" width="18" height="6" rx="3"/></svg>
                  ) : it.kind==='form' ? (
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
                  ) : it.kind==='submit' ? (
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                  ) : it.kind==='link' ? (
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 1 7 0l3 3a5 5 0 1 1-7 7l-3-3"/><path d="M14 11a5 5 0 0 1-7 0L4 8a5 5 0 1 1 7-7l3 3"/></svg>
                  ) : it.kind==='onclick' ? (
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20"/><path d="M5 12h14"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/></svg>
                  )}
                </span>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-gray-800 truncate">{it.label}</div>
                  {it.detail ? <div className="text-[11px] text-gray-500 truncate">{it.detail}</div> : null}
                  <div className="mt-1 inline-flex items-center gap-1">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${it.kind==='button' ? 'bg-blue-50 text-blue-700' : it.kind==='form' ? 'bg-amber-50 text-amber-700' : it.kind==='submit' ? 'bg-emerald-50 text-emerald-700' : it.kind==='link' ? 'bg-sky-50 text-sky-700' : it.kind==='onclick' ? 'bg-fuchsia-50 text-fuchsia-700' : 'bg-gray-50 text-gray-700'}`}>{it.kind}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <FieldLabel>Height (px)</FieldLabel>
        <input
          type="number"
          min={120}
          className="w-32 rounded-md border border-gray-300 p-1.5 text-sm"
          value={heightStr}
          onChange={(e)=> {
            const v = e.target.value;
            setHeightStr(v);
            const n = parseInt(v, 10);
            if (!Number.isNaN(n)) {
              // live update preview and persist upward
              setForm(prev => ({ ...prev, fixedHeight: n }));
              emit({ fixedHeight: Math.max(120, n) });
            }
          }}
          onBlur={()=> {
            const n = Math.max(120, parseInt(heightStr || '360', 10) || 360);
            setHeightStr(String(n));
            emit({ fixedHeight: n });
          }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <FieldLabel>Horizontal offset (px)</FieldLabel>
          <input
            type="number"
            className="w-32 rounded-md border border-gray-300 p-1.5 text-sm"
            value={offXStr}
            onChange={(e)=> setOffXStr(e.target.value)}
            onBlur={()=> { const n = parseInt(offXStr || '0',10) || 0; setOffXStr(String(n)); emit({ offsetX: n }); setForm(prev=>({...prev, offsetX: n})); }}
          />
        </div>
        <div className="space-y-2">
          <FieldLabel>Vertical offset (px)</FieldLabel>
          <input
            type="number"
            className="w-32 rounded-md border border-gray-300 p-1.5 text-sm"
            value={offYStr}
            onChange={(e)=> setOffYStr(e.target.value)}
            onBlur={()=> { const n = parseInt(offYStr || '0',10) || 0; setOffYStr(String(n)); emit({ offsetY: n }); setForm(prev=>({...prev, offsetY: n})); }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Toggle checked={!!form.bordered} onChange={()=> emit({ bordered: !form.bordered })} label="Show border" />
        <Toggle checked={!!form.shadow} onChange={()=> emit({ shadow: !form.shadow })} label="Shadow" />
        <Toggle checked={!!form.allowScroll} onChange={()=> emit({ allowScroll: !form.allowScroll })} label="Allow scrolling" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Toggle checked={!!form.responsiveFit} onChange={()=> emit({ responsiveFit: !form.responsiveFit })} label="Responsive fit to width" />
      </div>

      <div>
        <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-3">
          <span className="text-sm text-gray-800 inline-flex items-center gap-2">
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v7"/><path d="M18.36 6.64a2 2 0 0 1 0 2.83L9.5 18.33l-4 1 1-4 8.86-8.86a2 2 0 0 1 2.83 0z"/></svg>
            Remove background
          </span>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${form.removeBg ? 'bg-purple-600' : 'bg-gray-300'}`}
            onClick={()=> emit({ removeBg: !form.removeBg })}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${form.removeBg ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </label>
      </div>

      <div className="space-y-2">
        <FieldLabel>Corner radius</FieldLabel>
        <input type="range" min={0} max={32} value={form.radius || 0} onChange={(e)=> emit({ radius: parseInt(e.target.value,10)||0 })} className="w-full accent-purple-600" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Toggle checked={!!form.allowScripts} onChange={()=> emit({ allowScripts: !form.allowScripts })} label="Allow scripts" />
        <Toggle checked={!!form.allowSameOrigin} onChange={()=> emit({ allowSameOrigin: !form.allowSameOrigin })} label="Same origin" />
        <Toggle checked={!!form.allowPopups} onChange={()=> emit({ allowPopups: !form.allowPopups })} label="Popups" />
      </div>

      <div className="pt-2">
        <FieldLabel>Live preview</FieldLabel>
        <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3">
          <div ref={previewRef} className="relative">
            <div className="absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white/90 px-2 py-0.5 text-[11px] text-gray-700 shadow-sm backdrop-blur">
              <span className="font-medium">{dim.w}px × {dim.h}px</span>
            </div>
            <Embed {...form} html={debouncedHtml} />
          </div>
        </div>
      </div>
      <NewTemplateGalleryTry
        open={showTemplatesTry}
        onClose={()=> setShowTemplatesTry(false)}
        onInsert={async (item: TemplateItem)=>{
          try {
            const res = await fetch(item.url, { credentials: 'include' });
            const txt = await res.text();
            setForm(prev => ({ ...prev, html: txt }));
            emit({ html: txt });
            setShowTemplatesTry(false);
          } catch {}
        }}
      />
    </div>
  );
};

export default EmbedSettings;
